/**
 * Leitura da resposta de `POST /warehouse/routes/compare` e projeção do
 * desenho da rota **PARA O MAPA DO GALPÃO** — puro, sem React.
 *
 * ── O que mudou em 11/08 (M5-3 / regra 6), e por quê ─────────────────────────
 * Este arquivo dizia, no topo, que o desenho da rota NÃO podia ser o mapa
 * (célula × metro seria "regra de três proibida"). O dono decidiu o contrário,
 * com estas palavras: *"o desenho da rota DEVE usar o sistema de mapa para
 * calcular rota, mesmo que as rotas não estejam dispostas lado a lado
 * corretamente, devemos ainda sim usar o mapa para calcular e exibir."*
 *
 * E a regra de três deixou de existir: o motor passou a CONSTRUIR o mundo a
 * partir do próprio layout do mapa (`wms_map_layouts`), multiplicando cada
 * célula por **`map_cell_m`** — a única medida em metros que sobrou. Então a
 * volta é a divisão pelo MESMO fator, não uma conversão inventada aqui:
 *
 *     célula = metro ÷ map_cell_m          (`ToMapCellPoint`)
 *
 * `map_cell_m` vem na resposta do comparador (com `map_cell_fallback` dizendo
 * se veio do cadastro ou da rede de segurança). Nenhuma coordenada nasce neste
 * arquivo: os pontos, as pernas de 90° e a perna final continuam vindo prontos
 * do motor — o que fazemos é devolvê-los ao referencial em que o dono desenhou.
 *
 * ── A imperfeição do layout é ACEITA, não corrigida ──────────────────────────
 * "mesmo que as ruas não estejam dispostas lado a lado corretamente": se o
 * desenho tem uma rua torta, a rota aparece torta — e é o mesmo torto que o
 * motor cobrou. Uma correção cosmética aqui faria a linha da tela discordar do
 * número da tabela, que é o defeito que o dono não aceita: **uma fonte só**.
 */

/** Avisos do motor que a tela trata com destaque próprio (rota sobre o mapa). */
export const ROUTE_WARNING_MAP_CELL_FALLBACK = 'MAP_CELL_FALLBACK';
export const ROUTE_WARNING_EMPTY_MAP_LAYOUT = 'EMPTY_MAP_LAYOUT';
export const ROUTE_WARNING_UNPLACED_LOCATION = 'UNPLACED_LOCATION';
export const ROUTE_WARNING_BRUTE_FORCE_LIMIT = 'BRUTE_FORCE_LIMIT';
export const ROUTE_ALGORITHM_MANUAL = 'MANUAL';

/** Os avisos que valem uma tarja no topo (o resto vai para a lista miúda). */
const HIGHLIGHTED_WARNING_RULES = [
    ROUTE_WARNING_EMPTY_MAP_LAYOUT,
    ROUTE_WARNING_MAP_CELL_FALLBACK,
    ROUTE_WARNING_UNPLACED_LOCATION,
];

function toFinite(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

function normalizeWarnings(list) {
    if (!Array.isArray(list)) {
        return [];
    }
    return list
        .map((entry, index) =>
            typeof entry === 'string'
                ? { rule: `WARNING_${index + 1}`, message: entry }
                : { rule: entry?.rule || `WARNING_${index + 1}`, message: entry?.message || String(entry?.rule || '') },
        )
        .filter((entry) => entry.message);
}

function normalizeBreakdown(raw) {
    const source = raw && typeof raw === 'object' ? raw : {};
    return {
        distance_m: toFinite(source.distance_m) ?? 0,
        travel_sec: toFinite(source.travel_sec) ?? 0,
        vertical_sec: toFinite(source.vertical_sec) ?? 0,
        handling_sec: toFinite(source.handling_sec) ?? 0,
        turn_sec: toFinite(source.turn_sec) ?? 0,
        total_sec: toFinite(source.total_sec) ?? 0,
    };
}

function normalizePoint(raw) {
    const x = toFinite(raw?.x);
    const y = toFinite(raw?.y);
    return x === null || y === null ? null : { x, y };
}

function normalizePoints(list) {
    return (Array.isArray(list) ? list : []).map(normalizePoint).filter(Boolean);
}

function normalizeLeg(raw) {
    const from = normalizePoint(raw?.from);
    const to = normalizePoint(raw?.to);
    if (!from || !to) {
        return null;
    }
    return {
        code: raw?.code ?? null,
        side: raw?.side ?? null,
        level: toFinite(raw?.level),
        position: toFinite(raw?.position),
        from,
        to,
    };
}

function normalizeStep(raw, index) {
    return {
        order: toFinite(raw?.order) ?? index + 1,
        item_key: raw?.item_key !== undefined && raw?.item_key !== null ? String(raw.item_key) : String(index),
        item_id: raw?.item_id ?? null,
        seq: toFinite(raw?.seq),
        lpn_code: raw?.lpn_code ?? null,
        from_code: raw?.from_code ?? null,
        to_code: raw?.to_code ?? null,
        // Onde o equipamento FICA ao terminar este item — a origem do próximo
        // trecho (regra 5 do M5 r1). Substituiu o `anchor_code`, que presumia
        // volta à cabeceira a cada passo.
        end_code: raw?.end_code ?? null,
        level: toFinite(raw?.level),
        side: raw?.side ?? null,
        legs: (Array.isArray(raw?.legs) ? raw.legs : []).map((leg) => ({
            kind: leg?.kind ?? null,
            from_code: leg?.from_code ?? null,
            to_code: leg?.to_code ?? null,
            points: normalizePoints(leg?.points),
            distance_m: toFinite(leg?.distance_m) ?? 0,
        })),
        approach_legs: (Array.isArray(raw?.approach_legs) ? raw.approach_legs : []).map(normalizeLeg).filter(Boolean),
        breakdown: normalizeBreakdown(raw?.breakdown),
    };
}

function normalizeResult(raw, index) {
    const steps = (Array.isArray(raw?.steps) ? raw.steps : []).map(normalizeStep);
    const geometry = raw?.geometry && typeof raw.geometry === 'object' ? raw.geometry : {};
    return {
        algorithm: raw?.algorithm ? String(raw.algorithm) : `ALGO_${index + 1}`,
        available: raw?.available !== false,
        exact: raw?.exact === true,
        item_count: toFinite(raw?.item_count) ?? steps.length,
        start_code: raw?.start_code ?? null,
        // Onde a rota TERMINA: a origem projetada da PRÓXIMA rota (§6.4 + regra
        // 5). Não existe mais "âncora" de retorno presumido.
        end_code: raw?.end_code ?? null,
        sequence: Array.isArray(raw?.sequence) ? raw.sequence.map((key) => String(key)) : [],
        steps,
        breakdown: normalizeBreakdown(raw?.breakdown),
        geometry: {
            points: normalizePoints(geometry.points),
            approach_legs: (Array.isArray(geometry.approach_legs) ? geometry.approach_legs : [])
                .map(normalizeLeg)
                .filter(Boolean),
            final_leg: normalizeLeg(geometry.final_leg),
        },
        unavailable_reason: raw?.unavailable_reason ? (normalizeWarnings([raw.unavailable_reason])[0] ?? null) : null,
        warnings: normalizeWarnings(raw?.warnings),
    };
}

/**
 * Normaliza a resposta do comparador. Tolerância de ENVELOPE apenas (mesma
 * disciplina do `payload.js`): as chaves de DADO continuam exclusivamente as
 * do contrato — `results`, `breakdown`, `geometry`, `map_cell_m`, `warnings`.
 */
export function ParseRouteComparison(payload) {
    const root = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};
    const rawResults = Array.isArray(payload)
        ? payload
        : Array.isArray(root.results)
          ? root.results
          : Array.isArray(root.routes)
            ? root.routes
            : [];
    return {
        item_count: toFinite(root.item_count) ?? 0,
        start_code: root.start_code ?? null,
        // O câmbio célula↔metro que o motor REALMENTE usou. É ele que traz a
        // polilinha de volta ao referencial do mapa — sem ele não há desenho.
        map_cell_m: toFinite(root.map_cell_m),
        map_cell_fallback: root.map_cell_fallback === true,
        results: rawResults.map(normalizeResult),
        recommended: Array.isArray(root.recommended) ? root.recommended.map((key) => String(key)) : [],
        warnings: normalizeWarnings(root.warnings),
    };
}

/** Um resultado da comparação pelo nome do algoritmo. */
export function RouteResultOf(comparison, algorithm) {
    return (comparison?.results ?? []).find((result) => result.algorithm === algorithm) ?? null;
}

/** Todos os avisos que valem para a tela (os do lote + os do algoritmo em foco). */
export function RouteWarningsOf(comparison, result) {
    const seen = new Set();
    const merged = [];
    for (const warning of [...(comparison?.warnings ?? []), ...(result?.warnings ?? [])]) {
        const key = `${warning.rule}|${warning.message}`;
        if (!seen.has(key)) {
            seen.add(key);
            merged.push(warning);
        }
    }
    return merged;
}

/**
 * Separa os avisos em TARJA (o desenho não pode ser levado a sério) e lista
 * miúda. Mapa vazio, endereço sem posição e câmbio de rede de segurança mudam
 * o que a tela está dizendo — número estimado apresentado como exato é pior
 * que número ausente.
 */
export function SplitRouteWarnings(warnings) {
    const list = warnings ?? [];
    return {
        highlighted: list.filter((warning) => HIGHLIGHTED_WARNING_RULES.includes(warning.rule)),
        others: list.filter((warning) => !HIGHLIGHTED_WARNING_RULES.includes(warning.rule)),
    };
}

/* ────────────────────────────────────────────────────────────────────────────
 * Metros do motor → células do mapa
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Um ponto do motor (metros) na CÉLULA do mapa. É a divisão pelo mesmo
 * `map_cell_m` com que o motor multiplicou a célula ao montar o mundo — ida e
 * volta exatas, sem escala nova.
 *
 * `map_cell_m` ausente ou não-positivo devolve `null`: sem o câmbio não existe
 * posição no mapa, e chutar 1 m/célula desenharia a rota no lugar errado com
 * cara de certa.
 */
export function ToMapCellPoint(point, mapCellM) {
    const cell = toFinite(mapCellM);
    if (!point || !(cell > 0)) {
        return null;
    }
    return { x: point.x / cell, y: point.y / cell };
}

function toCellList(points, mapCellM) {
    const out = [];
    for (const point of points ?? []) {
        const cell = ToMapCellPoint(point, mapCellM);
        if (cell) {
            out.push(cell);
        }
    }
    return out;
}

function toCellLeg(leg, mapCellM) {
    const from = ToMapCellPoint(leg?.from, mapCellM);
    const to = ToMapCellPoint(leg?.to, mapCellM);
    return from && to ? { code: leg.code, side: leg.side, level: leg.level, from, to } : null;
}

/**
 * As PARADAS da rota (uma por item), já com a CÉLULA do mapa onde o desenho as
 * marca.
 *
 * A lista sai do primeiro resultado que cobre todos os itens — o conjunto de
 * paradas é o MESMO para todos os algoritmos (o que muda entre eles é só a
 * ordem), então isto é leitura, não escolha. É esta lista que o Supervisor
 * toca, na ordem, para montar a rota MANUAL: cada parada carrega o `item_key`
 * que o motor espera em `manual_sequence`.
 *
 * O ponto de marcação é a célula do CORREDOR em frente ao rack (é lá que o
 * equipamento encosta); item sem ponta em rack — transporte de chão — é
 * marcado na ORIGEM, que é onde o pallet está esperando.
 */
export function BuildRouteStops(comparison) {
    const mapCellM = comparison?.map_cell_m ?? null;
    const results = comparison?.results ?? [];
    const reference =
        results.find((result) => result.available && result.steps.length >= (comparison?.item_count ?? 0)) ??
        results.find((result) => result.available && result.steps.length > 0) ??
        null;
    if (!reference) {
        return [];
    }
    return reference.steps
        .map((step) => {
            const rackLeg = step.approach_legs[0] ?? null;
            const carry = step.legs.find((leg) => leg.kind === 'CARRY') ?? step.legs[step.legs.length - 1] ?? null;
            const point = rackLeg?.from ?? carry?.points?.[0] ?? null;
            const cell = ToMapCellPoint(point, mapCellM);
            return cell
                ? {
                      item_key: step.item_key,
                      seq: step.seq,
                      lpn_code: step.lpn_code,
                      from_code: step.from_code,
                      to_code: step.to_code,
                      level: rackLeg?.level ?? step.level,
                      side: rackLeg?.side ?? step.side,
                      rack_code: rackLeg?.code ?? null,
                      cell,
                      // A célula do BLOCO servido (a ponta da perna de 90°).
                      // Ela vem das paradas, e não só do algoritmo em foco,
                      // porque no modo MANUAL ainda não existe algoritmo — e o
                      // Supervisor precisa enxergar os blocos em jogo enquanto
                      // monta a ordem.
                      block_cell: ToMapCellPoint(rackLeg?.to, mapCellM),
                  }
                : null;
        })
        .filter(Boolean);
}

/**
 * O DESENHO da rota em coordenadas de CÉLULA, pronto para o `WarehouseMap`:
 * a polilinha do percurso, as pernas de 90° (que apontam a célula do bloco), a
 * perna FINAL (a que responde "ao chegar, para que lado eu viro"), o ponto de
 * partida e as paradas numeradas.
 *
 * A numeração é a ORDEM: a do algoritmo em foco, ou a que o Supervisor está
 * montando no modo manual. Parada sem número ainda não entrou na sequência.
 *
 * ⚠️ Montando a rota manual, a polilinha do algoritmo em foco NÃO é emprestada
 * (o chamador passa `result: null`): uma linha alheia embaixo dos cliques faria
 * o Supervisor achar que está corrigindo aquela rota — e ele está criando outra.
 */
export function BuildRouteMapOverlay({
    comparison,
    result = null,
    stops = [],
    manualMode = false,
    manualSequence = [],
} = {}) {
    const mapCellM = comparison?.map_cell_m ?? null;
    const orderByItem = new Map();
    if (manualMode) {
        for (const [index, key] of manualSequence.entries()) {
            orderByItem.set(String(key), index + 1);
        }
    } else {
        for (const step of result?.steps ?? []) {
            orderByItem.set(step.item_key, step.order);
        }
    }

    const polyline = toCellList(result?.geometry?.points, mapCellM);
    const approachLegs = (result?.geometry?.approach_legs ?? [])
        .map((leg) => toCellLeg(leg, mapCellM))
        .filter(Boolean);
    const finalLeg = toCellLeg(result?.geometry?.final_leg, mapCellM);

    const blockCells = [...approachLegs.map((leg) => leg.to), ...stops.map((stop) => stop.block_cell)].filter(Boolean);

    return {
        polyline,
        approach_legs: approachLegs,
        final_leg: finalLeg,
        // Os blocos que o trabalho TOCA: é o que o mapa mantém colorido
        // enquanto apaga o resto do galpão, para a rota ser a figura.
        block_cells: blockCells,
        // A partida é o `last_position` do equipamento (§6.4): o quadradinho de
        // onde a linha sai. Sem polilinha não há de onde sair.
        start: polyline.length > 0 && result?.start_code ? { code: result.start_code, cell: polyline[0] } : null,
        stops: stops.map((stop) => ({ ...stop, order: orderByItem.get(stop.item_key) ?? null })),
    };
}
